import {Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy} from '@angular/core';

import {MatIconModule} from '@angular/material/icon';
import {MatButtonModule} from '@angular/material/button';
import {FormsModule} from '@angular/forms';
import {MatInputModule} from '@angular/material/input';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatDatepickerInputEvent, MatDatepickerModule} from '@angular/material/datepicker';
import {LayoutElementUtil} from "../../../../service/layout/layout-element.service";

/**
 * Datum-/Uhrzeit-Auswahl für Zeitstempel.
 *
 * Ersetzt die nicht mehr gepflegte Library @angular-material-components/datetime-picker
 * (letzte Version für Angular 16) durch native Angular-Material-Bausteine:
 * MatDatepicker für das Datum + ein natives `<input type="time">` für die Uhrzeit.
 * Beide Werte werden zu einem Date kombiniert. Die öffentliche API bleibt unverändert.
 */
@Component({
  selector: 'app-datetime-picker',
  templateUrl: './datetime-picker.component.html',
  styleUrls: ['./datetime-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [MatFormFieldModule, MatInputModule, FormsModule, MatButtonModule, MatIconModule, MatDatepickerModule]
})
export class DatetimePickerComponent implements OnInit {

  @Output() dateChange: EventEmitter<Date> = new EventEmitter<Date>();
  @Input() showClearButton = false;
  @Input() disabled: boolean = false;
  @Input() date?: Date;
  @Input() showDefaultTime?: boolean;

  constructor(protected layoutElemeneUtil: LayoutElementUtil) {
  }

  ngOnInit(): void {
    // Initial ist kein Datum gesetzt
    this.date = undefined;
    if (this.layoutElemeneUtil.ReadonlyMode) {
      this.disabled = true;
    }
  }

  /** HH:mm des aktuellen Datums für das Zeit-Eingabefeld. */
  get timeString(): string {
    if (!this.date) {
      return '';
    }
    const hours = String(this.date.getHours()).padStart(2, '0');
    const minutes = String(this.date.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  }

  /** Basisdatum, wenn noch keines gewählt wurde: jetzt, auf die nächsten 5 Minuten gerundet. */
  private baseDate(): Date {
    if (this.date) {
      return new Date(this.date);
    }
    const now = new Date();
    now.setMinutes(Math.ceil(now.getMinutes() / 5) * 5, 0, 0);
    return now;
  }

  /** Datum gewählt: bestehende Uhrzeit (oder Standard) beibehalten. */
  onDateSelected(event: MatDatepickerInputEvent<Date>): void {
    const selected = event.value;
    if (!selected) {
      return;
    }
    const base = this.baseDate();
    const combined = new Date(selected);
    combined.setHours(base.getHours(), base.getMinutes(), 0, 0);
    this.date = combined;
    this.dateChange.emit(this.date);
  }

  /** Uhrzeit geändert: bestehendes Datum (oder heute) beibehalten. */
  onTimeChanged(value: string): void {
    if (!value) {
      return;
    }
    const [hours, minutes] = value.split(':').map(part => parseInt(part, 10));
    if (isNaN(hours) || isNaN(minutes)) {
      return;
    }
    const combined = new Date(this.date ?? this.baseDate());
    combined.setHours(hours, minutes, 0, 0);
    this.date = combined;
    this.dateChange.emit(this.date);
  }

  clear(): void {
    this.date = undefined;
    this.dateChange.emit(this.date);
  }
}
